import * as React from 'react';
import { observer } from 'mobx-react-lite';
import ItemAttachment from 'owa-service/lib/contract/ItemAttachment';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import { getUserMailboxInfo, ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import {
    IconSize,
    AttachmentIconViewBase,
    AttachmentDocInfoViewBase,
} from 'owa-attachment-common-view';
import { TypeOfAttachment, getTypeOfAttachment } from 'owa-attachment-type';
import { AttachmentClass, lazyAddAndInitializeAttachments } from 'owa-attachment-model-store';
import getAttachmentClass from 'owa-attachment-model-store/lib/actions/initialization/getAttachmentClass';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { AttachmentPolicyInfo, getAttachmentPolicyInfo } from 'owa-attachment-policy';
import { previewAttachmentInPopOut } from 'owa-attachment-deeplink-popout';
import { AccessIssue, getCombinedAccessIssue } from 'owa-attachment-policy-access-issue-checker';
import AttachmentBlockedCalloutButton from './AttachmentBlockedCalloutButton';
import * as styles from './AttachmentDocView.scss';
import classNames from 'classnames';
import { getFirstLineTitleInner } from 'owa-attachment-view/lib/utils/getAttachmentInfo';

const ITEM_CLASS_NOTE = 'IPM.Note';

export default observer(function AttachmentDocView({
    attachment,
    parentItemId,
    isShortDocView,
    useCloudIcon,
    showInfoIcon,
}: {
    attachment: AttachmentType;
    parentItemId: ClientItemId;
    isShortDocView: boolean;
    useCloudIcon: boolean;
    showInfoIcon: boolean;
}) {
    const attachmentId = attachment.AttachmentId.Id;
    const accessIssue = getCombinedAccessIssue();
    const isAttachmentOpeningAllowed =
        attachmentId && accessIssue != AccessIssue.ReadOnlyPlusAttachmentsBlocked;

    const onClickCallback = React.useCallback(() => {
        isAttachmentOpeningAllowed &&
            previewAttachmentInPopOut(parentItemId, attachmentId, false /* isEdit */);
    }, [attachment]);

    const attachmentInfo = {
        Id: attachmentId,
        mailboxInfo: getUserMailboxInfo(),
    };

    initializeFileAttachment(attachmentInfo, attachment);
    const attachmentModel = getAttachment(attachmentInfo);

    if (!attachmentModel) {
        // if attachment hasn't loaded yet, show nothing
        return null;
    }

    const attachmentType = getTypeOfAttachment(attachment);
    const attachmentPolicyInfo: AttachmentPolicyInfo = getAttachmentPolicyInfo(
        attachment,
        true /* isWacPreviewSupportedOnPlatform */
    );
    const attachmentClass = getAttachmentClass(attachmentPolicyInfo, attachment);

    const isCloudy: boolean = attachmentType === TypeOfAttachment.Reference;
    const isFolder = isCloudy ? !!(attachment as ReferenceAttachment).AttachmentIsFolder : false;

    let embeddedItemClass: string | null = null;
    if (!isCloudy && attachmentType === TypeOfAttachment.Mail) {
        embeddedItemClass = (attachment as ItemAttachment).EmbeddedItemClass || ITEM_CLASS_NOTE;
    }

    const firstLineTitle = getFirstLineTitleInner(attachmentModel, isCloudy);
    const attachmentDocViewClassNames = classNames(styles.attachmentDocView, {
        [styles.attachmentDocViewHover]: isAttachmentOpeningAllowed,
    });

    return (
        <div className={classNames(attachmentDocViewClassNames)} onClick={onClickCallback}>
            <AttachmentIconViewBase
                contentType={attachment.ContentType ? attachment.ContentType : ''}
                customizeStyle={styles.attachmentIcon}
                embeddedItemClass={embeddedItemClass ? embeddedItemClass : undefined}
                fileName={attachment.Name ? attachment.Name : ''}
                iconSize={IconSize.extraSmall}
                isBlocked={attachmentClass === AttachmentClass.Blocked}
                isCloudy={isCloudy}
                isFolder={isFolder}
                useCloudIcon={useCloudIcon}
            />
            <div className={styles.attachmentText}>
                <AttachmentDocInfoViewBase
                    firstLineText={attachment.Name ? attachment.Name : ''}
                    firstLineTitle={firstLineTitle}
                    isShortDocView={isShortDocView}
                />
            </div>
            {showInfoIcon && !isAttachmentOpeningAllowed && <AttachmentBlockedCalloutButton />}
        </div>
    );
});

async function initializeFileAttachment(
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType
) {
    const addAndInitializeAttachmentModels = await lazyAddAndInitializeAttachments.import();
    addAndInitializeAttachmentModels(
        [
            {
                attachmentId,
                attachment: attachment,
            },
        ],
        true /* isReadOnly */
    );
}
